import { useCallback, useState } from 'react';
import { toast } from 'sonner';

interface UseCopyToClipboardOptions {
  /** Mensagem do toast em caso de sucesso. Default: "Copiado". */
  successMessage?: string;
  /** Mensagem em caso de falha. Default: "Não foi possível copiar". */
  errorMessage?: string;
  /** Tempo (ms) que o estado `copied` permanece true após copiar. Default: 2000. */
  resetMs?: number;
  /** Mostrar toast automaticamente. Default: true. */
  silent?: boolean;
}

/**
 * Hook utilitário para copiar texto para a área de transferência.
 * Padroniza feedback (toast + estado `copied` para trocar ícones).
 *
 * Centraliza o que estava duplicado em CopyableEmail, CobrancaDetail,
 * RascunhoStatusBar, TermoActions, OperadoresDelegadosCard, CodeBlock.
 */
export function useCopyToClipboard(options: UseCopyToClipboardOptions = {}) {
  const {
    successMessage = 'Copiado',
    errorMessage = 'Não foi possível copiar',
    resetMs = 2000,
    silent = false,
  } = options;
  const [copied, setCopied] = useState(false);

  const copy = useCallback(
    async (value: string | null | undefined) => {
      if (!value) return false;
      try {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        if (!silent) toast(successMessage);
        if (resetMs > 0) {
          window.setTimeout(() => setCopied(false), resetMs);
        }
        return true;
      } catch {
        if (!silent) toast.error(errorMessage);
        return false;
      }
    },
    [silent, successMessage, errorMessage, resetMs],
  );

  return { copy, copied };
}
